/**
 * Выбор микрофона и наушников.
 *
 * Свёрнут в одну строку (просьба Evgeniy 30.08.2026): два больших селекта в
 * шапке пульта нужны раз в неделю, а место ели каждый день. Выбор ЗАПОМИНАЕТСЯ
 * в браузере и подхватывается при следующем входе сам — кнопка «сохранить»
 * не нужна, сохраняется каждый выбор. Если запомненную гарнитуру унесли,
 * пульт молча остаётся на системном устройстве — падать из-за неё нельзя.
 *
 * Список запрашивается уже после того, как ядро получило доступ к микрофону,
 * иначе браузер отдаёт устройства без названий (§7.9) и выбирать приходится вслепую.
 */

import { useEffect, useRef, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { AudioDeviceManager } from '@cc/softphone-core';
import type { AudioDeviceList } from '@cc/softphone-core';

import type { CallVolume } from '../volume.js';

/** Запомненный выбор устройств — переживает F5 и следующую смену. */
const STORAGE_KEY = 'cc.desk.audio-devices';

/** undefined в патче стирает ключ: JSON.stringify не пишет undefined-поля. */
type SavedDevices = { inputId?: string | undefined; outputId?: string | undefined };

function readSaved(): SavedDevices {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    return JSON.parse(raw) as SavedDevices;
  } catch {
    return {};
  }
}

function writeSaved(patch: SavedDevices): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readSaved(), ...patch }));
  } catch {
    // хранилище недоступно — выбор просто не переживёт перезагрузку
  }
}

export interface DevicePickerProps {
  inputId: string | undefined;
  outputId: string | undefined;
  onSelectInput: (deviceId: string) => void;
  onSelectOutput: (deviceId: string) => void;
  /** Громкость гудков и голоса клиента, % — у каждого диспетчера своя */
  volume: CallVolume;
  onVolumeChange: (volume: CallVolume) => void;
}

export function DevicePicker({
  inputId,
  outputId,
  onSelectInput,
  onSelectOutput,
  volume,
  onVolumeChange,
}: DevicePickerProps): React.JSX.Element {
  const devices = useAudioDevices();
  const [open, setOpen] = useState(false);

  /**
   * Восстановление запомненного выбора — один раз, когда браузер отдал
   * НАСТОЯЩИЙ список (после разрешения на микрофон deviceId непустые).
   * Устройство ищется в списке: запомненная гарнитура могла уехать домой,
   * и навязывать несуществующий id значит оставить пульт без звука.
   */
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    const listReady =
      devices.inputs.some((d) => d.deviceId !== '') ||
      devices.outputs.some((d) => d.deviceId !== '');
    if (!listReady) return;
    restored.current = true;

    const saved = readSaved();
    if (
      saved.inputId !== undefined &&
      inputId === undefined &&
      devices.inputs.some((d) => d.deviceId === saved.inputId)
    ) {
      onSelectInput(saved.inputId);
    }
    if (
      saved.outputId !== undefined &&
      outputId === undefined &&
      devices.outputs.some((d) => d.deviceId === saved.outputId)
    ) {
      onSelectOutput(saved.outputId);
    }
  }, [devices, inputId, outputId, onSelectInput, onSelectOutput]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className="flex max-w-96 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
        title="Микрофон, наушники и громкость"
      >
        <Settings2 className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        <span className="truncate">
          {deviceName(devices.inputs, inputId)} · {deviceName(devices.outputs, outputId)}
        </span>
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <DeviceSelect
        label="Микрофон"
        options={devices.inputs}
        value={inputId}
        onChange={(id) => {
          writeSaved({ inputId: id === '' ? undefined : id });
          onSelectInput(id);
        }}
      />
      <DeviceSelect
        label="Наушники"
        options={devices.outputs}
        value={outputId}
        onChange={(id) => {
          writeSaved({ outputId: id === '' ? undefined : id });
          onSelectOutput(id);
        }}
      />
      <VolumeSlider
        label="Гудки"
        value={volume.ring}
        onChange={(ring) => {
          onVolumeChange({ ...volume, ring });
        }}
      />
      <VolumeSlider
        label="Голос клиента"
        value={volume.talk}
        onChange={(talk) => {
          onVolumeChange({ ...volume, talk });
        }}
      />
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
          }}
          className="h-9 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-500"
        >
          Готово
        </button>
        <span className="text-xs text-slate-400">Выбор запомнится</span>
      </div>
    </div>
  );
}

/**
 * Ползунок громкости. Меняет звук сразу, пока тянешь: подбирать громкость
 * гудка на слух удобнее во время звонка, а не «сохранил и позвонил снова».
 */
function VolumeSlider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (percent: number) => void;
}): React.JSX.Element {
  return (
    <label className="flex min-w-40 flex-col gap-1 text-xs font-medium text-slate-500">
      <span className="flex justify-between">
        {label}
        <span className="tabular-nums text-slate-700">{value}%</span>
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        onChange={(event) => {
          onChange(Number(event.target.value));
        }}
        className="h-9 accent-blue-600"
      />
    </label>
  );
}

/** Короткое имя для свёрнутой строки: без длинных скобочных хвостов браузера. */
function deviceName(options: MediaDeviceInfo[], value: string | undefined): string {
  if (value === undefined || value === '') return 'По умолчанию';
  const device = options.find((d) => d.deviceId === value);
  if (device === undefined || device.label === '') return 'Выбрано';
  // «Наушники (G30 BT SPEAKER) (Bluetooth)» → «Наушники (G30 BT SPEAKER)»
  return device.label.replace(/^По умолчанию\s*[—-]\s*/i, '').replace(/\s*\([^()]*\)\s*$/, '');
}

function DeviceSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: MediaDeviceInfo[];
  value: string | undefined;
  onChange: (deviceId: string) => void;
}): React.JSX.Element {
  return (
    <label className="flex min-w-48 flex-col gap-1 text-xs font-medium text-slate-500">
      {label}
      <select
        value={value ?? ''}
        onChange={(event) => { onChange(event.target.value); }}
        className="h-9 max-w-64 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900"
      >
        <option value="">Системное по умолчанию</option>
        {options.map((device) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label !== '' ? device.label : device.deviceId}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Список устройств пульта.
 *
 * Перечитывается не только на монтировании. Браузер не отдаёт устройства, пока
 * не выдано разрешение на микрофон, а события «список изменился» при выдаче
 * разрешения НЕ БЫВАЕТ: `devicechange` срабатывает на втыкание и вынимание
 * гарнитуры, но не на нажатие «Разрешить».
 *
 * Прецедент 28.08.2026: в окне инкогнито разрешение выдали, а оба списка так
 * и остались с единственным пунктом «Системное по умолчанию» — выбрать
 * гарнитуру было нечем до перезагрузки страницы.
 *
 * Поэтому подписываемся ещё и на смену состояния разрешения, а на случай
 * браузеров без такого API оставлен разовый повтор.
 */
function useAudioDevices(): AudioDeviceList {
  const [list, setList] = useState<AudioDeviceList>({ inputs: [], outputs: [] });

  useEffect(() => {
    const manager = new AudioDeviceManager({
      ...(typeof navigator !== 'undefined' ? { mediaDevices: navigator.mediaDevices } : {}),
      onDevicesChanged: setList,
    });
    manager.start();

    let alive = true;
    const refresh = (): void => {
      void manager.list().then((next) => {
        if (alive) setList(next);
      });
    };
    refresh();

    // Разрешение выдали — устройства появились и у них появились названия
    let permission: PermissionStatus | null = null;
    const onPermissionChange = (): void => { refresh(); };
    if (typeof navigator !== 'undefined') {
      void navigator.permissions
        .query({ name: 'microphone' })
        .then((status) => {
          if (!alive) return;
          permission = status;
          status.addEventListener('change', onPermissionChange);
          // Разрешение могли выдать между монтированием и ответом на запрос
          if (status.state === 'granted') refresh();
        })
        .catch(() => {
          // Браузер не умеет спрашивать про микрофон — обойдёмся повтором ниже
        });
    }

    // Запасной путь для браузеров без Permissions API: один отложенный повтор.
    // Двух секунд хватает, чтобы человек нажал «Разрешить», и это дешевле
    // постоянного опроса
    const retry = setTimeout(refresh, 2000);

    return () => {
      alive = false;
      clearTimeout(retry);
      permission?.removeEventListener('change', onPermissionChange);
      manager.stop();
    };
  }, []);

  return list;
}
